import { useState } from 'react';
import { Box, Grid, Modal, Paper, Typography, Button } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import {
  CustomerVerification,
  Menu,
  OrderSetupModal,
  ResetModal,
  TwoFactorAuthenticationModal,
  useCustomerVerification,
} from '@components';
import { DetailView } from './DetailView';
import { setShippingAddress } from '@store/slices/order';
import { SubTitle } from '@components';
import { useCustomer } from '@hooks/useCustomer';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SendToMobileOutlinedIcon from '@mui/icons-material/SendToMobileOutlined';
import { CustomerBlackListedData } from '@models';
import CustomerEmailChangeModal from './CustomerEmailChangeModal';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { TerminateAccountModal } from '@components/TerminateAccountModal';
import { useVerifyUserMutation } from '@services';

interface Props {
  blacklistedData?: CustomerBlackListedData;
}

export const CustomerDetail = ({ blacklistedData }: Props) => {
  const [blackListModal, setBlackListModal] = useState(false);
  const [openTerminateAccountModal, setOpenTerminateAccountModal] = useState(false);
  const [openEmailChangeModal, setOpenEmailChangeModal] = useState(false);

  const { customer, customerAttributes } = useCustomer();
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetModalContent, setResetModalContent] = useState({
    attributesToDelete: [''],
    header: '',
    body: '',
  });
  const activeUser = useAppSelector((state) => state.activeUser.value);
  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'customer');
  const hasWriteAccess = userAccessStatus?.role > 1;
  const hasTerminateCustomerAccess = userAccessStatus.role > 3;
  const [modalOpen, setModalOpen] = useState(false);
  const [verifyUser] = useVerifyUserMutation();
  const [facode, setFacode] = useState('');
  const [verificationError, setVerificationError] = useState<{ hasError: boolean; errorDesc: string }>({ hasError: false, errorDesc: '' });
  const dispatch = useAppDispatch();
  const { verified } = useCustomerVerification();

  const verify2FA = () => {
    setVerificationError({ hasError: false, errorDesc: '' });

    verifyUser({
      email: activeUser.user.email,
      code: facode,
    })
      .unwrap()
      .then((res) => {
        if (res.data || res.code === 200) {
          setModalOpen(false);
          setOpenTerminateAccountModal(true);
        } else {
          setVerificationError({ hasError: true, errorDesc: res.message ?? 'Unable to verify user' });
        }
      })
      .catch((err) => {
        setVerificationError({ hasError: true, errorDesc: err.message ?? 'Unable to verify user' });
        console.error('===verify2FA error===', err);
      });
  };

  const handleOrderModalClose = () => {
    setIsOrderModalOpen(false);
    dispatch(setShippingAddress(undefined));
  };

  const handleResetModalClose = () => {
    setIsResetModalOpen(false);
  };

  let modalAttributesToDelete: string[] = [];
  let modalHeader = '';
  let modalBody = '';

  const openResetModal = (attribute: string) => {
    switch (attribute) {
      case 'Phone':
        modalAttributesToDelete = ['phonenumberverifieddate'];
        modalHeader = 'Reset Mobile Number';

        modalBody =
          'This will remove the existing mobile number from the customers account, The customer will need to re-verify their mobile number before being able to order again. Proceed?';

        break;
      case 'Password':
        modalAttributesToDelete = ['password'];
        modalHeader = 'Reset Password';

        modalBody =
          'This will send an email to the customer. The customer will then be able to change their password through the password reset link. Proceed?';

        break;
      default:
        modalAttributesToDelete = [];
        modalHeader = 'Error';
    }

    setResetModalContent({
      attributesToDelete: modalAttributesToDelete,
      header: modalHeader,
      body: modalBody,
    });

    setIsResetModalOpen(true);
  };

  const BlackListModal = () => {
    return (
      <Grid container alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
        <Grid xs={4} borderRadius={'10px'} justifyContent={'center'} bgcolor={'#f4f4f4'} item>
          <Grid p={2} container justifyContent={'center'} borderBottom={1} borderColor={'#ebebeb'}>
            <Typography variant={'h6'} sx={{ color: 'red' }}>
              ADD CUSTOMER TO BLACKLIST
            </Typography>
          </Grid>
          <Grid p={3} container direction={'column'} alignItems={'flex-start'} bgcolor={'#f4f4f4'} item>
            <Typography mb={2.5} fontWeight={'bold'}>
              Mobile Number: {customer?.phone}
            </Typography>
            <Typography fontWeight={'bold'}>Email: {customer?.email} </Typography>
          </Grid>
          <Typography mx={3} my={2}>
            Clicking accept below will add the customer's mobile and email to our blacklist database. They won't be able to order once
            accepted.
          </Typography>
          <Grid container p={2} pb={2.5} justifyContent={'space-around'}>
            <Button
              onClick={() => setBlackListModal(false)}
              disableRipple
              variant="contained"
              sx={{ backgroundColor: '#cbcbcb', width: '30%' }}
            >
              Cancel
            </Button>
            <Button onClick={() => setBlackListModal(false)} variant="contained" sx={{ width: '30%' }}>
              Agree
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const menuItems = [
    {
      label: 'Reset Mobile Number',
      onMenuItemClick: () => openResetModal('Phone'),
      icon: <SendToMobileOutlinedIcon sx={{ mr: 1 }} />,
      dataTestId: 'reset-mobile-menu-item',
    },
    {
      label: 'Reset Password',
      onMenuItemClick: () => openResetModal('Password'),
      icon: <LockResetIcon sx={{ mr: 1 }} />,
      dataTestId: 'reset-password-menu-item',
    },
    {
      label: 'Change Email',
      onMenuItemClick: () => setOpenEmailChangeModal(true),
      icon: <AlternateEmailIcon sx={{ mr: 1 }} />,
      dataTestId: 'change-email-menu-item',
    },
  ];

  if (window?.FEATURE_FLAGS?.BLACKLIST_POST_ECOMM_6) {
    menuItems.push({
      label: blacklistedData?.isBlacklisted ? `Remove Blacklist` : `Blacklist`,
      onMenuItemClick: () => setBlackListModal(true),
      icon: <BlockOutlinedIcon sx={{ mr: 1 }} />,
      dataTestId: 'black-list-option',
    });
  }

  if (hasTerminateCustomerAccess) {
    menuItems.push({
      label: `Terminate Account`,
      onMenuItemClick: () => {
        setModalOpen(true);
        setFacode('');
        setVerificationError({ hasError: false, errorDesc: '' });
      },
      icon: <DeleteForeverIcon sx={{ mr: 1 }} />,
      dataTestId: 'terminate-account-menu-item',
    });
  }

  return (
    <>
      <Box>
        <SubTitle
          title="Customer Details"
          adornment={
            hasWriteAccess && (
              <Box display="flex" gap={1}>
                <CustomerVerification />
                <Menu menuItems={menuItems} disabled={!verified} tooltipTitle={!verified ? 'Verify Customer to view' : 'Settings'} />
              </Box>
            )
          }
        />
        <Modal onClose={() => setBlackListModal(false)} open={blackListModal}>
          {BlackListModal()}
        </Modal>
        <CustomerEmailChangeModal isOpen={openEmailChangeModal} onClose={() => setOpenEmailChangeModal(false)} customer={customer} />
        <Paper elevation={2}>
          {customer && (
            <Box p={1}>
              {<DetailView isBlackListedData={blacklistedData} bigCCustomerAttributes={customerAttributes} resetModal={openResetModal} />}
            </Box>
          )}
        </Paper>
      </Box>
      <OrderSetupModal isOpen={isOrderModalOpen} onClose={handleOrderModalClose} />
      {customer && (
        <>
          <ResetModal
            isOpen={isResetModalOpen}
            customer={customer}
            onClose={handleResetModalClose}
            bigCCustomerAttributes={customerAttributes}
            modalContent={resetModalContent}
          />
          <TerminateAccountModal
            isOpen={openTerminateAccountModal}
            bigCCustomerAttributes={customerAttributes}
            customer={customer}
            onClose={() => {
              setOpenTerminateAccountModal(false);
            }}
            setModal={setOpenTerminateAccountModal}
          />
          <TwoFactorAuthenticationModal
            isErrorCode={verificationError.hasError}
            errorDesc={verificationError.errorDesc}
            onSubmit={verify2FA}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            fieldValue={facode}
            onInputChange={(e) => setFacode(e.target.value)}
            modalDescription={''}
          />
        </>
      )}
    </>
  );
};
